import type RouteHandlers from './RouteHandlers';
import getParameters from './getParameters';
import navigateToRoute, { isNavigating, getForceRouteHandler } from './navigateToRoute';
import { getCurrentRoute } from './routerInstance';
import {
    getLastProcessedRoute,
    updateLastProcessedRouteHandlers,
    resetLastProcessedRoute,
} from './lastProcessedRoute';
import { trace } from 'owa-trace';

export default function onRouteChanged(
    route: string,
    handlers: RouteHandlers,
    args: any[],
    setModuleForVdir?: () => void
): Promise<any> {
    const newParameters = getParameters(route, args);
    const lastProcessedRoute = getLastProcessedRoute();
    const currentRoute = getCurrentRoute();
    const forceRouteHandler = getForceRouteHandler();

    // Don't call the route handler if we've already processed this route (e.g.
    // because the route change was due an application state change)
    const routesAreDifferent = isDifferentRoute(currentRoute, lastProcessedRoute.route);
    const navigating = isNavigating();
    const shouldHandleNavigation = routesAreDifferent && (!navigating || forceRouteHandler);
    trace.info(
        `onRouteChanged route: ${route}, lastProcessedRoute: ${lastProcessedRoute.route}, currentRoute: ${currentRoute}, routesAreDifferent: ${routesAreDifferent} navigating:${navigating} forceRouteHandler:${forceRouteHandler} shouldHandleNavigation:${shouldHandleNavigation}`,
        'router'
    );

    if (shouldHandleNavigation) {
        let shouldPreventNavigation: Promise<boolean> | boolean = false;
        if (lastProcessedRoute.handlers?.onNavigateFrom) {
            trace.info(`Invoking ${lastProcessedRoute.route} onNavigateFrom`, 'router');
            shouldPreventNavigation = lastProcessedRoute.handlers.onNavigateFrom(
                lastProcessedRoute.route,
                lastProcessedRoute.parameters,
                currentRoute,
                newParameters.named
            );
            trace.info(`shouldPreventNavigation: ${shouldPreventNavigation}`, 'router');
        }

        const handleNavigation = (preventNavigation: boolean) => {
            if (preventNavigation) {
                // Restore the old route without processing the new route's handlers
                trace.info(`Restoring route: ${lastProcessedRoute.route}`, 'router');
                navigateToRoute(lastProcessedRoute.route?.slice(1) || []);
            } else {
                setModuleForVdir?.();

                // Invoke the handler for the new route
                trace.info(`Invoking ${route} onNavigateTo`, 'router');
                handlers.onNavigateTo(newParameters.named, newParameters.all);

                // Update records for the last processed route
                resetLastProcessedRoute();
                updateLastProcessedRouteHandlers(handlers, newParameters);
            }
            return Promise.resolve();
        };

        if (typeof shouldPreventNavigation === 'boolean') {
            return handleNavigation(shouldPreventNavigation);
        } else {
            return shouldPreventNavigation.then(handleNavigation);
        }
    } else {
        // In the event this is a callback due to state change,
        // we still want to store the handlers and such for later cleanup
        trace.info(`Navigation not handled: ${JSON.stringify(newParameters)}`, 'router');
        updateLastProcessedRouteHandlers(handlers, newParameters);
        return Promise.resolve();
    }
}

function isDifferentRoute(newRoute: string[], oldRoute: string[] | undefined) {
    return (newRoute || []).join('/') !== (oldRoute || []).join('/');
}
